"use client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dynamic from "next/dynamic";
import {
  getAllUserFollowing,
  getFollowingUsersPosts,
  getUsersForTagging,
} from "@/api/posts";
import { getPlayFeed, getPostById } from "@/api/hotple-content";
import ContentMasonryCard from "@/components/content-comps/content-masonry-cards";
import FirstVisitBand from "@/components/play-comps/first-visit-band";
import { useRouter, useSearchParams } from "next/navigation";
import { enqueueSnackbar } from "notistack";
import { useAuthContext } from "@/context/auth/useAuthContext";
import ContentSkeleton from "@/components/loaders/skeletons/content-skeleton";
import NoNotesAvailable from "@/components/create-notes-manager-comps/no-notes-available";
import Iconify from "@/components/ui/Iconify";
import InfiniteScroll from "react-infinite-scroll-component";
import { useGlobalContext } from "@/context/global/GlobalContext";
import ContentUserList from "@/components/content-comps/content-user-list";
import { OriginModal, Modal } from "@/components/origin-modal";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { BASE_URL } from "../../../../config";
import { jwtDecode } from "jwt-decode";
import Cookies from "js-cookie";
import { getUserDataByUserId } from "@/api/user";
import {
  ROUTES,
  buildContentPermanentPath,
  isVideoContent,
} from "@/constants/routes";
import useContentModalNavigation from "@/hooks/useContentModalNavigation";
import {
  buildPlayContext,
  isPlayableContextContent,
  savePlayContext,
} from "@/utils/play-context";

// 글을 눌렀을 때 뜨는 창은 **열 때만** 받아 온다.
// 이 창이 댓글 입력·애니메이션 라이브러리(131KB)까지 끌고 오는데,
// 정작 창은 `isOpen` 일 때만 그려진다. 목록만 보는 사람은 받을 이유가 없다.
const ContentViewModal = dynamic(
  () => import("@/components/content-comps/content-view-modal"),
  { ssr: false }
);

const ERROR_MESSAGE = "오류가 발생했습니다.";
const END_OF_LIST_TEXT = "--x-- 목록 끝 --x--";
const LOGIN_RETURN_TO_KEY = "hotpleLoginReturnTo";
const DESKTOP_PLAY_DETAIL_QUERY = "(min-width: 768px)";
const LOCAL_API_HOSTNAMES = new Set(["localhost", "127.0.0.1"]);

const isSafeReturnPath = (path) =>
  typeof path === "string" && path.startsWith("/") && !path.startsWith("//");

const isLocalBrowser = () =>
  typeof window !== "undefined" && LOCAL_API_HOSTNAMES.has(window.location.hostname);

const getLoginSuccessUrl = () =>
  isLocalBrowser() ? "/api/backend/login/success" : `${BASE_URL}/login/success`;

const syncAuthCookiesFromToken = (token) => {
  if (!token) return false;

  try {
    const user = jwtDecode(token);

    if (!user?.userId || !user?.role || !user?.exp) return false;

    const cookieOptions = {
      expires: new Date(user.exp * 1000),
      secure: process.env.NODE_ENV === "production",
      sameSite: "strict",
      path: "/",
    };

    Cookies.set("userId", user.userId, cookieOptions);
    Cookies.set("role", user.role, cookieOptions);
    return true;
  } catch {
    localStorage.removeItem("jwtToken");
    Cookies.remove("userId", { path: "/" });
    Cookies.remove("role", { path: "/" });
    return false;
  }
};

const PlayFeed = ({ scrollToTop, isScrolled, initialPostId, initialFeed }) => {
  const playerRef = useRef(null);
  const router = useRouter();
  const popupRef = useRef(null);
  const {
    user,
    openLoginModal,
    isLoginModalOpen,
    dispatch,
    handleLoginSession,
    setPendingDisplayNameUser,
  } = useAuthContext();
  const { search, filter, setFilter } = useGlobalContext();
  const searchParams = useSearchParams();
  const urlSearchParam = searchParams.get("q") || searchParams.get("search") || "";
  const placeIdParam = searchParams.get("placeId") || "";
  const placeUniqueNumberParam = searchParams.get("placeUniqueNumber") || "";
  const [showOnlyImageNotes, setShowOnlyImageNotes] = useState(false);
  const [dynamicSiteLoader, setDynamicSiteLoader] = useState(false);
  const [isSkeletonVisible, setIsSkeletonVisible] = useState(false);
  const [currentUserFollowing, setCurrentUserFollowing] = useState([]);
  const [selectedPostInModal, setSelectedPostInModal] = useState(null);
  // 서버가 미리 받아 준 첫 페이지로 시작한다. 이게 없으면 첫 그림이 비어 있어서
  // 「아직 올린 콘텐츠가 없어요」가 잠깐 스친다(카드 목록이 별도 상태라서 그렇다).
  // page.js 가 좁힌 목록일 때는 안 보내므로 여기서 또 걸러낼 필요가 없다.
  const [allPosts, setAllPosts] = useState(() =>
    Array.isArray(initialFeed?.data) ? initialFeed.data : []
  );
  const [allPostsBackup, setAllPostsBackup] = useState([]);
  const [allUsersForTagging, setAllUsersForTagging] = useState([]);
  const [searchParam, setSearchParam] = useState(null);
  const [filterParam, setFilterParam] = useState(null);
  const [sortParam, setSortParam] = useState(null);
  const [page, setPage] = useState(1);
  const [popupStyle, setPopupStyle] = useState({});
  const [originalStyle, setOriginalStyle] = useState({});
  const [isAnimating, setIsAnimating] = useState(false);
  const [postUsers, setContentUsers] = useState({});
  const [showContentUsers, setShowContentUsers] = useState(false);
  const [isPiP, setIsPiP] = useState(false);
  const isFetchedFirstTime = useRef(false);
  const previousData = useRef([]);
  // const containerRef = useRef(null);
  const {
    isOpen,
    isClosing,
    position,
    handleOriginModalTriggerClick,
    handleOriginModalClose,
    setIsOpen,
  } = OriginModal();

  const postPageMap = useRef({});

  const resetModalState = useCallback(() => {
    setIsAnimating(false);
    setPopupStyle(originalStyle);
    setSelectedPostInModal(null);
    handleOriginModalClose();
  }, [handleOriginModalClose, originalStyle]);

  // useEffect(() => {
  //   if (!containerRef.current || allPosts?.length === 0) return;

  //   const Masonry = window.Masonry || require("masonry-layout");

  //   const imgLoad = imagesLoaded(containerRef.current);
  //   imgLoad.on("always", () => {
  //     new Masonry(containerRef.current, {
  //       itemSelector: ".grid-item",
  //       columnWidth: ".grid-sizer",
  //       gutter: 10,
  //       percentPosition: true,
  //       // horizontalOrder: true,
  //     });
  //   });

  //   return () => {
  //     imgLoad.off("always");
  //   };
  // }, [allPosts]);

  const getUserDetails = async () => {
    try {
      const apiResponse = await fetch(getLoginSuccessUrl(), {
        method: "GET",
        credentials: "include",
      });

      const res = await apiResponse.json();

      if (!res?.authenticated || !res?.token) return;

      const token = res.token;
      const decodedUser = jwtDecode(token);
      const apiUser = Array.isArray(res?.user) ? res.user[0] : res?.user;
      const user = {
        ...decodedUser,
        ...(apiUser || {}),
        userId: decodedUser?.userId || apiUser?.id,
        id: apiUser?.id || decodedUser?.userId,
        role: decodedUser?.role || apiUser?.role,
        username: decodedUser?.username || apiUser?.username,
        name: decodedUser?.name || apiUser?.name,
        profilePicture:
          decodedUser?.profilePicture || apiUser?.profilePicture,
        exp: decodedUser?.exp,
        iat: decodedUser?.iat,
      };

      Cookies.set("userId", user?.userId, {
        expires: new Date(user.exp * 1000),
        secure: process.env.NODE_ENV === "production",
        sameSite: "strict",
        path: "/",
      });

      Cookies.set("role", user?.role, {
        expires: new Date(user.exp * 1000),
        secure: process.env.NODE_ENV === "production",
        sameSite: "strict",
        path: "/",
      });

      localStorage.setItem("jwtToken", token);
      handleLoginSession(user?.userId, "success");
      dispatch({
        type: "LOGIN",
        payload: {
          user,
        },
      });

      if (res?.isNewUser || !String(user?.name || "").trim()) {
        setPendingDisplayNameUser?.(user);
        openLoginModal();
        return;
      }

      const returnTo = sessionStorage.getItem(LOGIN_RETURN_TO_KEY);
      if (isSafeReturnPath(returnTo)) {
        sessionStorage.removeItem(LOGIN_RETURN_TO_KEY);
        router.replace(returnTo);
      }
      // onclose();
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message || error?.message || "Error",
        {
          variant: "error",
        }
      );
    }
  };

  const openPostModal = (event, postData) => {
    const target = event?.currentTarget || event?.target;
    const rect = target.getBoundingClientRect();
    setOriginalStyle({
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    });
    setPopupStyle({
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    });
    setTimeout(() => {
      setIsAnimating(true);
    }, 0);
    handleOriginModalTriggerClick(event);
    setSelectedPostInModal(postData);
    setIsPiP(false);
  };

  const openPostModalFromNavigation = useCallback((postData) => {
    setOriginalStyle({});
    setPopupStyle({});
    setIsAnimating(true);
    setSelectedPostInModal(postData);
    setIsPiP(false);
    setIsOpen(true);
  }, [setIsOpen]);

  const restoreModalFromNavigation = useCallback((context) => {
    const restoredPost = allPosts.find(
      (post) =>
        post?.id === context?.contentId ||
        post?._id === context?.contentId ||
        post?.publicId === context?.publicId
    );

    if (restoredPost) {
      openPostModalFromNavigation(restoredPost);
      return;
    }

    if (!context?.contentId) return;

    getPostById(context.contentId)
      .then((post) => {
        if (post?.id) openPostModalFromNavigation(post);
      })
      .catch((error) => {
        console.error("Failed to restore modal content:", error);
      });
  }, [allPosts, openPostModalFromNavigation]);

  const { openModalRoute, closeModalRoute } = useContentModalNavigation({
    isModalOpen: !!selectedPostInModal,
    fallbackPath: ROUTES.current.play,
    onModalRouteEnter: restoreModalFromNavigation,
    onModalRouteExit: resetModalState,
  });

  const handlePostCardClick = (e, postData) => {
    e?.stopPropagation?.();
    const shouldOpenDesktopModal =
      typeof window !== "undefined" &&
      window.matchMedia(DESKTOP_PLAY_DETAIL_QUERY).matches;

    const permanentPath = buildContentPermanentPath(postData);
    if (permanentPath) {
      if (isPlayableContextContent(postData)) {
        const currentQuery =
          searchParam ||
          search ||
          urlSearchParam ||
          "";
        const rawFilter =
          filterParam ||
          filter ||
          searchParams?.get("filter") ||
          searchParams?.get("theme") ||
          "";
        const currentFilter = rawFilter === "all" ? "" : rawFilter;

        savePlayContext(
          buildPlayContext({
            contextType: currentQuery ? "search" : "play",
            initialVideo: postData,
            feed: allPosts,
            query: currentQuery,
            category: currentFilter,
            sourceParams: {
              search: currentQuery,
              filter: currentFilter,
              sort: sortParam || searchParams?.get("sort") || "",
              placeId: placeIdParam,
              placeUniqueNumber: placeUniqueNumberParam,
            },
          })
        );
      }

      if (shouldOpenDesktopModal) {
        openModalRoute({
          canonicalPath: permanentPath,
          backgroundPath: ROUTES.current.play,
          source: "play_feed",
          contentKind: isVideoContent(postData) ? "play" : "post",
          content: postData,
        });
        openPostModal(e, postData);
        return;
      }

      router.push(permanentPath, { scroll: false });
      return;
    }

    openPostModal(e, postData);
  };

  const closePopup = useCallback(() => {
    closeModalRoute();
    resetModalState();
  }, [closeModalRoute, resetModalState]);

  const fetchPosts = async ({ pageParam = 1 }) => {
    const search = urlSearchParam;
    const filter = searchParams.get("filter");
    const normalizedFilter = filter === "all" ? "" : filter;
    const sort = searchParams.get("sort");

    const params = {
      page: pageParam,
      ...(search && { search }),
      ...(placeIdParam && { placeId: placeIdParam }),
      ...(placeUniqueNumberParam && { placeUniqueNumber: placeUniqueNumberParam }),
      ...(normalizedFilter && { filter: normalizedFilter }),
      ...(sort && { sort }),
    };
    const response = await getPlayFeed(params);
    setDynamicSiteLoader(false);
    setIsSkeletonVisible(false);
    return {
      data: response?.data,
      nextPage: pageParam + 1,
      totalCount: response?.totalCount,
    };
  };

  const fetchFollowingUsersPosts = async (userId, params) => {
    try {
      const response = await getFollowingUsersPosts(userId, params);
      // setAllPosts(response);
      return {
        data: response,
        nextPage: params?.page + 1,
        totalCount: response?.length,
      };
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
        error?.message ||
        ERROR_MESSAGE,
        {
          variant: "error",
        }
      );
    } finally {
      setDynamicSiteLoader(false);
      setIsSkeletonVisible(false);
    }
  };

  const fetchUserProfileById = async (userId, params) => {
    try {
      const response = await getUserDataByUserId(userId, params);
      let likedVideos = response?.likes?.map((_l) => _l?.post);
      // setAllPosts(likedVideos);
      return {
        data: likedVideos,
        nextPage: params?.page + 1,
        totalCount: likedVideos?.length,
      };
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
        error?.message ||
        ERROR_MESSAGE,
        {
          variant: "error",
        }
      );
    } finally {
      setDynamicSiteLoader(false);
      setIsSkeletonVisible(false);
    }
  };

  const getFetchFn = (filter, searchTerm) => {
    if (searchTerm) {
      scrollToTop();
      return ({ pageParam = 1 }) => fetchPosts({ pageParam });
    }
    if (filter === "all") {
      // scrollToTop();
      return ({ pageParam = 1 }) => fetchPosts({ pageParam });
    }

    if (filter === "following") {
      return ({ pageParam = 1 }) =>
        fetchFollowingUsersPosts(user?.userId, { page: pageParam });
    }

    if (filter === "favorites") {
      return ({ pageParam = 1 }) =>
        fetchUserProfileById(user?.username, { page: pageParam });
    }

    return ({ pageParam = 1 }) => fetchPosts({ pageParam });
  };

  const queryFn = useMemo(() => {
    const searchTerm = urlSearchParam;
    return getFetchFn(filter, searchTerm);
  }, [filter, user, searchParams, urlSearchParam]);

  // 서버가 준 첫 페이지를 쓸 수 있는 상황인지 — 좁힌 목록이면 쓰면 안 된다.
  const hasNarrowingFeedParams = Boolean(
    urlSearchParam ||
      searchParams.get("search") ||
      searchParams.get("q") ||
      searchParams.get("filter") ||
      searchParams.get("sort") ||
      placeIdParam ||
      placeUniqueNumberParam ||
      (filter && filter !== "all")
  );

  const {
    data: postData,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isRefetching,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: [
      "posts",
      {
        search: searchParams.get("search"),
        q: searchParams.get("q"),
        placeId: searchParams.get("placeId"),
        placeUniqueNumber: searchParams.get("placeUniqueNumber"),
        filter:
          (searchParams.get("filter") === "all" ? "" : searchParams.get("filter")) ||
          (filter === "all" ? "" : filter),
        sort: searchParams.get("sort"),
      },
      filter === "all" ? "" : filter,
    ],
    queryFn, // 👈 dynamic function based on filter
    getNextPageParam: (lastPage) =>
      lastPage?.data?.length < 20 ? undefined : lastPage?.nextPage,
    enabled:
      !!filter ||
      searchParams.size === 0 ||
      !!urlSearchParam ||
      (!!searchParams.get("filter") && searchParams.get("filter") !== "user") ||
      !!searchParams.get("sort") ||
      !!initialPostId,
    // 서버가 미리 받아 둔 첫 페이지를 씨앗으로 쓴다 → 브라우저가 같은 것을 다시 안 묻는다.
    // ⚠️ 검색어·분류·정렬·특정 가게로 좁힌 목록일 때는 쓰면 안 된다(다른 목록이다).
    ...(initialFeed && !hasNarrowingFeedParams
      ? {
          initialData: {
            pages: [
              {
                data: initialFeed.data,
                nextPage: 2,
                totalCount: initialFeed.totalCount,
              },
            ],
            pageParams: [1],
          },
          // ⚠️ 서버는 쿠키 없이 물어본다. 로그인한 분의 목록이 다를 수 있으니
          // "이미 낡은 것"으로 표시해 뒤에서 곧바로 다시 받아 오게 한다.
          // 화면은 서버가 만든 것으로 즉시 뜨고, 바뀌면 조용히 갈린다.
          initialDataUpdatedAt: 0,
        }
      : {}),
    keepPreviousData: true,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const fetchAllUsersForTagging = async () => {
    try {
      const response = await getUsersForTagging();
      if (Array.isArray(response)) setAllUsersForTagging(response);
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
        error?.message ||
        ERROR_MESSAGE,
        {
          variant: "error",
        }
      );
    }
  };

  const fetchAllUserFollowing = async (userId) => {
    if (!userId) return;
    try {
      const response = await getAllUserFollowing(userId);
      if (Array.isArray(response)) setCurrentUserFollowing(response);
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
        error?.message ||
        ERROR_MESSAGE,
        {
          variant: "error",
        }
      );
    }
  };

  const completeRefresh = () => {
    setIsSkeletonVisible(true);
    scrollToTop();
    fetchAllUserFollowing(user?.userId);
    postPageMap.current = {};
    previousData.current = [];
    isFetchedFirstTime.current = false;

    // Then trigger a fresh refetch
    refetch();

    setTimeout(() => {
      setIsSkeletonVisible(false);
    }, 1000 * 1);
  };

  // 이 목록은 **창(데스크톱에서 글을 열었을 때)** 의 댓글칸에서 「@사람」을 고를 때만 쓴다.
  // 예전에는 화면을 열자마자 받았는데, 4.4KB 짜리가 0.5~0.6초나 걸리고
  // 릴스로 들어온 분은 대부분 글을 안 열고 나간다. 창이 처음 열릴 때 한 번만 받는다.
  const taggingFetchedRef = useRef(false);
  useEffect(() => {
    if (!selectedPostInModal || taggingFetchedRef.current) return;
    taggingFetchedRef.current = true;
    fetchAllUsersForTagging();
  }, [selectedPostInModal]);

  useEffect(() => {
    if (searchParams.get("filter") == "user") {
      setShowContentUsers(true);
    } else {
      setShowContentUsers(false);
    }
    if (
      urlSearchParam ||
      searchParams.get("filter") ||
      searchParams.get("sort")
    ) {
      setSearchParam(urlSearchParam || searchParam);
      setFilterParam(searchParams.get("filter") || filterParam);
      setSortParam(searchParams.get("sort") || sortParam);
      postPageMap.current = {};
    }
  }, [searchParams, page]);

  useEffect(() => {
    if (user?.userId) fetchAllUserFollowing(user.userId);
  }, [user]);

  useEffect(() => {
    const currentPostIdInView = initialPostId;
    const allPosts = postData?.pages?.flatMap((page) => page?.data);

    if (!currentPostIdInView) return;

    const postInPage = allPosts?.find((post) => post?.id === currentPostIdInView);
    if (postInPage) {
      setSelectedPostInModal(postInPage);
      return;
    }

    getPostById(currentPostIdInView)
      .then((post) => {
        if (post?.id) setSelectedPostInModal(post);
      })
      .catch((error) => {
        console.error("Failed to fetch permanent URL post:", error);
      });
  }, [postData, initialPostId]);

  useEffect(() => {
    const handleClickOutside = (e) => { };
    if (selectedPostInModal) setIsOpen(true);
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [selectedPostInModal]);

  useEffect(() => {
    setAllPosts(
      showOnlyImageNotes
        ? allPostsBackup.filter((post) =>
          post?.media[0]?.mediaType?.includes("image")
        )
        : allPostsBackup
    );
  }, [showOnlyImageNotes]);

  useEffect(() => {
    if (!isFetchedFirstTime.current && postData)
      isFetchedFirstTime.current = true;

    if (postData) {
      // const flatData = postData.pages.flatMap((page) => page?.data ?? []);
      // previousData.current = flatData;
      // setAllPosts(flatData);

      previousData.current = postData?.pages?.flatMap((page) => page?.data);
      setAllPosts([...previousData.current]);

      const filteredUsers = previousData.current.filter((item) => item.isUser);

      // console.log("Filtered Users:", filteredUsers);

      setContentUsers((prev) => {
        const users = { ...prev };
        filteredUsers.forEach((user) => {
          users[user.id] = user;
        });
        return users;
      });
    }
  }, [postData]);

  useEffect(() => {
    setPage(1);
    setContentUsers({});
    postPageMap.current = {};
  }, [search]);

  useEffect(() => {
    const showLogin = searchParams.get("showLogin");
    const returnTo = searchParams.get("returnTo");

    if (showLogin === "true") {
      if (isSafeReturnPath(returnTo)) {
        sessionStorage.setItem(LOGIN_RETURN_TO_KEY, returnTo);
      }

      const token = localStorage.getItem("jwtToken");
      if (syncAuthCookiesFromToken(token)) {
        router.replace(isSafeReturnPath(returnTo) ? returnTo : ROUTES.current.play);
        return;
      }

      if (!isLoginModalOpen) {
        openLoginModal();
      }

      // Remove the query parameter from the URL
      const params = new URLSearchParams(searchParams);
      params.delete("showLogin");
      params.delete("returnTo");
      params.delete("login");

      const nextQuery = params.toString();
      router.replace(nextQuery ? `${ROUTES.current.play}?${nextQuery}` : ROUTES.current.play, {
        shallow: true,
      });
    }
  }, [searchParams, isLoginModalOpen, router]);
  // 구글 로그인 뒤 서버 세션을 우리 토큰으로 바꾸는 요청.
  // ⚠️ 예전에는 **누구에게나 무조건** 불렀다. 릴스로 들어오는 분은 대부분 비로그인이라
  //    매번 헛걸음이었다. 이제 필요한 때만 부른다.
  //      ① 방금 구글에서 돌아왔다(?login=1 — 백엔드 successRedirect 가 붙인다)
  //      ② 우리 토큰이 이미 있다  ③ 우리 쿠키가 이미 있다
  useEffect(() => {
    const justLoggedIn =
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("login") === "1";
    const hasToken =
      typeof window !== "undefined" && !!localStorage.getItem("jwtToken");
    const hasCookie = !!Cookies.get("userId");

    if (justLoggedIn || hasToken || hasCookie) {
      getUserDetails();
    }
    setFilter("all");
  }, []);

  useEffect(() => {
    scrollToTop();
  }, [filter]);

  const handleEnablePIP = () => {
    setIsPiP(true);
  };

  const handleDisablePIP = () => {
    setIsPiP(false);
  };

  if (showContentUsers) {
    return <ContentUserList postUsers={postUsers} />;
  }

  // 한 번이라도 받아 온 적이 있어야 「없어요」를 그린다.
  // 받아 오기 전의 빈 목록은 "없는 것"이 아니라 "아직 모르는 것"이다.
  const hasLoadedFeedOnce = Boolean(postData) || allPosts.length > 0;

  const filteredData = allPosts.filter(
    (item) => item?.isPublic || user?.userId === item?.user?.id
  )

  // 「더 불러오는 중」 동그라미를 보일지 (예전 접힘 부품의 조건 그대로)
  const isLoadingMoreVisible =
    isFetchedFirstTime.current &&
    isFetching &&
    !isRefetching &&
    !isFetchingNextPage;

  return (
    <div className="w-full inner-div">
      {/* 처음 온 사람에게만 한 줄. 닫으면 다시 안 뜬다. */}
      <FirstVisitBand isLoggedIn={Boolean(user?.userId)} />
      <div className="w-full pb-0 max-w-full">
        {(!isFetchedFirstTime.current && isFetching && !postData) ||
          isSkeletonVisible ? (
          <ContentSkeleton variant="vertical-feed" />
        ) : (
          <div className="w-full flex flex-col">
            <div
              aria-hidden={!isLoadingMoreVisible}
              className="grid w-full overflow-hidden transition-[grid-template-rows] duration-1000 ease-out"
              style={{
                gridTemplateRows: isLoadingMoreVisible ? "1fr" : "0fr",
              }}
            >
              <div className="min-h-0">
                <div className="w-full h-32 -mt-4 flex items-center justify-center">
                  <Iconify
                    icon="line-md:loading-twotone-loop"
                    className="opacity-80 !h-8 !w-8 animate-spin"
                  />
                </div>
              </div>
            </div>

            <div className="w-full h-full">
              {filteredData?.length > 0 ? (
                <InfiniteScroll
                  scrollableTarget="scrollable-box"
                  dataLength={filteredData?.length}
                  // next={() => {
                  //   setPage(page + 1)
                  // }}
                  next={fetchNextPage}
                  hasMore={hasNextPage}
                  scrollThreshold={0.6}
                  className="w-full"
                  loader={
                    <div className="pt-6 pb-20">
                      <ContentSkeleton variant="vertical-feed" />
                    </div>
                  }
                  endMessage={
                    <div className="pt-6 pb-20 lg:pb-8 text-black/40 dark:text-white/40 text-xs flex items-center justify-center">
                      {END_OF_LIST_TEXT}
                    </div>
                  }
                >
                  {/* 자바스크립트로 높이를 재지 않는 폭포수 배치.
                      예전 라이브러리는 서버에서 화면 너비를 0 으로 보기 때문에
                      서버가 그린 자리와 브라우저가 그린 자리가 어긋났다(고칠 수 없었다).
                      카드 높이가 세 규격(3:4 · 1:1 · 4:3)으로 고정돼 있어
                      왼쪽 줄부터 차례로 채워도 줄 끝 어긋남이 작다. */}
                  <div
                    className="play-feed-columns"
                    // 글 수보다 열을 더 만들지 않는다(빈 열 방지). 서버도 개수를 안다.
                    style={{ "--feed-max-cols": Math.max(2, Math.ceil(filteredData.length / 2)) }}
                  >
                    {filteredData.map((post, i) => (
                      <div className="play-feed-column-item" key={`post-${i}`}>
                        <ContentMasonryCard
                          post={post}
                          variant="play-feed"
                          setAllPosts={setAllPosts}
                          priority={i < 5}
                          href={buildContentPermanentPath(post) || undefined}
                          onClick={(e) => handlePostCardClick(e, post)}
                        />
                      </div>
                    ))}
                  </div>
                  {/* <div ref={containerRef} className="masonry-container">
                    <div className="grid-sizer" />
                    {filteredData.map((post, i) => (
                      <div key={i} className="grid-item">
                        <ContentMasonryCard
                          key={`post-${i}`}
                          post={post}
                          setAllPosts={setAllPosts}
                          href={buildContentPermanentPath(post) || undefined}
          onClick={(e) => handlePostCardClick(e, post)}
                        />
                      </div>
                    ))}
                  </div> */}
                </InfiniteScroll>
              ) : hasLoadedFeedOnce ? (
                <NoNotesAvailable />
              ) : null}

              <div className="w-fit h-fit fixed z-30 bottom-16 lg:bottom-5 right-5 lg:right-10 flex flex-col gap-2">
                {showOnlyImageNotes && (
                  <div
                    className={`bg-white dark:bg-dark_bg_grey dark:border-gray-700 dark:hover:bg-dark_bg_grey w-10 h-10 rounded-full flex items-center justify-center border hover:bg-gray-200 transition duration-100 ease-in-out cursor-pointer ${showOnlyImageNotes && "border-black"
                      }`}
                    onClick={() => setShowOnlyImageNotes((prev) => !prev)}
                  >
                    <Iconify
                      icon={
                        showOnlyImageNotes
                          ? "mdi:image-check-outline"
                          : "mdi:image-outline"
                      }
                      className={"!h-5 !w-5"}
                    />
                  </div>
                )}
                {isScrolled ? (
                  <div
                    className="bg-white dark:bg-dark_bg_grey dark:border-gray-700 dark:hover:bg-dark_bg_grey w-10 h-10 rounded-full flex items-center justify-center border hover:bg-gray-200 transition duration-100 ease-in-out cursor-pointer"
                    onClick={scrollToTop}
                  >
                    <Iconify icon={"carbon:arrow-up"} className={"!h-5 !w-5"} />
                  </div>
                ) : (
                  <div
                    className={`bg-white dark:bg-dark_bg_grey dark:border-gray-700 dark:hover:bg-dark_bg_grey w-10 h-10 rounded-full flex items-center justify-center border hover:bg-gray-200 transition duration-100 ease-in-out cursor-pointer ${showOnlyImageNotes && "border-black"
                      }`}
                    onClick={() => setShowOnlyImageNotes((prev) => !prev)}
                  >
                    <Iconify
                      icon={
                        showOnlyImageNotes
                          ? "mdi:image-check-outline"
                          : "mdi:image-outline"
                      }
                      className={"!h-5 !w-5"}
                    />
                  </div>
                )}

                <div
                  className="bg-white dark:bg-dark_bg_grey dark:border-gray-700 dark:hover:bg-dark_bg_grey w-10 h-10 rounded-full flex items-center justify-center border hover:bg-gray-200 transition duration-100 ease-in-out cursor-pointer"
                  onClick={completeRefresh}
                >
                  <Iconify
                    icon={"uiw:reload"}
                    className={`!h-4 !w-4 ${dynamicSiteLoader && "animate-spin"
                      }`}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {isOpen && (
        <Modal
          handleClose={closePopup}
          isClosing={isClosing}
          position={position}
          isOpen={isOpen || !!selectedPostInModal}
          isPiP={isPiP}
        >
          <ContentViewModal
            ref={popupRef}
            allPosts={allPosts}
            closePopup={closePopup}
            setAllPosts={setAllPosts}
            currentUserFollowing={currentUserFollowing}
            setCurrentUserFollowing={setCurrentUserFollowing}
            allUsersForTagging={allUsersForTagging}
            selectedPostInModal={selectedPostInModal}
            setSelectedPostInModal={setSelectedPostInModal}
            completeRefresh={completeRefresh}
            isAnimating={isAnimating}
            popupStyle={popupStyle}
            isPiP={isPiP}
            setIsPiP={setIsPiP}
            playerRef={playerRef}
            handleDisablePIP={handleDisablePIP}
            handleEnablePIP={handleEnablePIP}
            openVideoInPlay={false}
          />
        </Modal>
      )}
    </div>
  );
};

export default PlayFeed;
